import { useRef, useState } from "react";
import { fmt, rcFrame, type Pt, type WallInput } from "@tasduvar/engine";
import { toSvg, type Transform } from "./geometry";
import { snap } from "./snap";

type Geom = WallInput["geometry"];
type Param = Extract<Geom, { mode: "parametric" }>;
type Rc = Extract<Geom, { mode: "rc-cantilever" }>;

/** Tutamaç: model koordinatı, ipucu ve başlangıç geometrisine (dx, dy) sürüklemesini uygulayan dönüşüm */
export interface Grip { id: string; at: Pt; hint: string; cursor: string; apply: (g: Geom, dx: number, dy: number) => Geom }

const MIN_T = 0.1;   // en ince kalınlık / yükseklik (m)
const clamp = (v: number, lo: number, hi = Infinity): number => Math.min(hi, Math.max(lo, v));

/**
 * Parametrik taş kesitin tutamaçları (Cem 2026-10-01: "noktaları tutup çekiştiremiyoruz"). Her köşe kendi ölçüsünü değiştirir;
 * kesit parametrik kalır (Akıllı tasarım, metraj, derzler çalışmaya devam eder). Koordinatlar `parametricToPolygon` ile aynı.
 */
export function paramGrips(g: Param): Grip[] {
  const hf = g.foundation?.height ?? 0, toe = g.foundation?.toeExtension ?? 0, mf = g.frontBatter;
  const Hb = g.steps.reduce((s, st) => s + st.height, 0);
  const xF = (yBody: number): number => toe + mf * yBody;
  const grips: Grip[] = [];
  const edit = (fn: (x: Param) => void) => (g0: Geom): Geom => { const x = structuredClone(g0) as Param; fn(x); return x; };
  grips.push({ id: "front-top", at: { x: xF(Hb), y: hf + Hb }, hint: "ön yüz eğimi · yükseklik", cursor: "move",
    // Ön yüz eğimi değişirken arka yüz yerinde kalır (kalınlıklar ön yüzden ölçüldüğü için Δm·y kadar düzeltilir; taban B sabit)
    apply: (g0, dx, dy) => edit((x) => {
      x.steps[0]!.height = clamp(snap(g.steps[0]!.height + dy), MIN_T);
      const h2 = x.steps.reduce((s, st) => s + st.height, 0);
      const m2 = clamp(Math.round(((mf * Hb + dx) / h2) * 1000) / 1000, 0, 1), dm = m2 - mf;
      x.frontBatter = m2;
      let y = h2;
      for (const st of x.steps) { st.topThickness = clamp(snap(st.topThickness - dm * y), MIN_T); y -= st.height; st.bottomThickness = clamp(snap(st.bottomThickness - dm * y), MIN_T); }
    })(g0) });
  let yTop = Hb;
  g.steps.forEach((st, i) => {
    const yBot = yTop - st.height;
    grips.push({ id: `top-back-${i}`, at: { x: xF(yTop) + st.topThickness, y: hf + yTop }, hint: i === 0 ? "üst kalınlık · yükseklik" : `${i + 1}. kademe üst kalınlığı`, cursor: i === 0 ? "move" : "ew-resize",
      apply: (g0, dx, dy) => edit((x) => { const s = x.steps[i]!; s.topThickness = clamp(snap(s.topThickness + dx), MIN_T); if (i === 0) s.height = clamp(snap(s.height + dy), MIN_T); })(g0) });
    const last = i === g.steps.length - 1;
    const xb = xF(yBot) + st.bottomThickness, slopeDrop = last && !g.foundation && (g.baseSlope ?? 0) > 0 ? -(g.baseSlope ?? 0) * xb : 0;
    grips.push({ id: `bottom-back-${i}`, at: { x: xb, y: hf + yBot + slopeDrop }, hint: last ? "taban (alt) kalınlığı" : `${i + 1}. kademe alt kalınlığı · kademe kotu`, cursor: last ? "ew-resize" : "move",
      apply: (g0, dx, dy) => edit((x) => {
        const s = x.steps[i]!; s.bottomThickness = clamp(snap(s.bottomThickness + dx), MIN_T);
        // kademe sınırı: yukarı sürükleme bu kademeyi kısaltır, alttakini uzatır (toplam yükseklik sabit)
        if (!last) { const n = x.steps[i + 1]!, d = clamp(snap(dy), -(n.height - MIN_T), s.height - MIN_T); s.height -= d; n.height += d; }
      })(g0) });
    yTop = yBot;
  });
  if (g.foundation) {
    const f = g.foundation, back = toe + g.steps[g.steps.length - 1]!.bottomThickness;
    grips.push({ id: "fnd-heel", at: { x: back + f.heelExtension, y: hf }, hint: "temel topuk çıkıntısı · temel yüksekliği", cursor: "move",
      apply: (g0, dx, dy) => edit((x) => { x.foundation!.heelExtension = clamp(snap(f.heelExtension + dx), 0); x.foundation!.height = clamp(snap(f.height + dy), MIN_T); })(g0) });
    grips.push({ id: "fnd-toe", at: { x: 0, y: hf }, hint: "temel burun çıkıntısı · temel yüksekliği", cursor: "move",
      apply: (g0, dx, dy) => edit((x) => { x.foundation!.toeExtension = clamp(snap(f.toeExtension - dx), 0); x.foundation!.height = clamp(snap(f.height + dy), MIN_T); })(g0) });
  }
  return grips;
}

/** Betonarme konsol tutamaçları: taban uçları, gövde alt köşeleri, gövde tepesi, kayma dişi. Koordinatlar `rcToPolygon` ile aynı (burun 0,0). */
export function rcGrips(g: Rc): Grip[] {
  const f = rcFrame(g);
  const edit = (fn: (x: Rc) => void) => (g0: Geom): Geom => { const x = structuredClone(g0) as Rc; fn(x); x.stem.topThickness = Math.min(x.stem.topThickness, x.stem.bottomThickness); return x; };
  const grips: Grip[] = [
    { id: "heel", at: { x: f.B, y: f.tf }, hint: "topuk boyu · taban kalınlığı", cursor: "move",
      apply: (g0, dx, dy) => edit((x) => { x.base.heelLength = clamp(snap(g.base.heelLength + dx), 0); x.base.thickness = clamp(snap(g.base.thickness + dy), MIN_T); })(g0) },
    { id: "toe", at: { x: 0, y: f.tf }, hint: "burun boyu · taban kalınlığı", cursor: "move",
      apply: (g0, dx, dy) => edit((x) => { x.base.toeLength = clamp(snap(g.base.toeLength - dx), 0); x.base.thickness = clamp(snap(g.base.thickness + dy), MIN_T); })(g0) },
    // Gövde alt köşeleri: ön köşe burunla, arka köşe topukla takas edilir (taban genişliği B sabit kalır)
    { id: "stem-front", at: { x: f.xStemFront, y: f.tf }, hint: "gövde alt kalınlığı (burundan)", cursor: "ew-resize",
      apply: (g0, dx) => edit((x) => { const d = clamp(snap(dx), -g.base.toeLength, g.stem.bottomThickness - MIN_T); x.base.toeLength = g.base.toeLength + d; x.stem.bottomThickness = g.stem.bottomThickness - d; })(g0) },
    { id: "stem-back", at: { x: f.xStemBack, y: f.tf }, hint: "gövde alt kalınlığı (topuktan)", cursor: "ew-resize",
      apply: (g0, dx) => edit((x) => { const d = clamp(snap(dx), MIN_T - g.stem.bottomThickness, g.base.heelLength); x.stem.bottomThickness = g.stem.bottomThickness + d; x.base.heelLength = g.base.heelLength - d; })(g0) },
    { id: "stem-top", at: { x: g.stem.taperSide === "front" ? f.xTopFront : f.xTopBack, y: f.yTop }, hint: "gövde üst kalınlığı · gövde yüksekliği", cursor: "move",
      apply: (g0, dx, dy) => edit((x) => { x.stem.topThickness = clamp(snap(g.stem.topThickness + (g.stem.taperSide === "front" ? -dx : dx)), MIN_T, g.stem.bottomThickness); x.stem.height = clamp(snap(g.stem.height + dy), 0.5); })(g0) },
  ];
  if (g.key) {
    const k = g.key;
    grips.push({ id: "key", at: { x: k.offsetFromToe + k.width / 2, y: -k.depth }, hint: "kayma dişi konumu · derinliği", cursor: "move",
      apply: (g0, dx, dy) => edit((x) => { x.key!.offsetFromToe = clamp(snap(k.offsetFromToe + dx), 0, f.B - k.width); x.key!.depth = clamp(snap(k.depth - dy), MIN_T); })(g0) });
  }
  return grips;
}

export function gripsOf(g: Geom): Grip[] {
  if (g.mode === "parametric") return paramGrips(g);
  if (g.mode === "rc-cantilever") return rcGrips(g);
  return [];
}

/** Ekran pikseli → SVG birimi çarpanı (PolygonEditor ile aynı) */
function pxScale(el: Element): number {
  const svg = el.closest("svg");
  const ctm = svg && "getScreenCTM" in svg ? (svg as SVGSVGElement).getScreenCTM?.() : null;
  return ctm && ctm.a ? 1 / ctm.a : 1;
}

/**
 * Kesit tutamaçları (AutoCAD "grip"): köşeyi tut-sürükle → ilgili ölçü değişir, 5 cm ızgaraya oturur. Sürükleme başındaki
 * geometri sabit tutulur, toplam yer değiştirme ona uygulanır (çizim yeniden ölçeklense de kayma birikmez). ORTHO: baskın eksen.
 */
export function ParamGrips({ t, geometry, onChange, ortho }: { t: Transform; geometry: Geom; onChange: (g: Geom) => void; ortho?: boolean }): JSX.Element {
  const drag = useRef<{ grip: Grip; start: Geom; client: { x: number; y: number }; k: number; scale: number } | null>(null);
  const [hot, setHot] = useState<string | null>(null);
  const grips = gripsOf(geometry);
  return (
    <g data-part="section-grips">
      {grips.map((gr) => {
        const s = toSvg(t, gr.at), on = hot === gr.id;
        return (
          <g key={gr.id}>
            <rect data-handle={gr.id} x={s.x - 5} y={s.y - 5} width={10} height={10} fill={on ? "#1F4E79" : "#fff"} stroke="#1F4E79" strokeWidth={1.5}
              style={{ cursor: gr.cursor, touchAction: "none" }} aria-label={gr.hint}
              onPointerEnter={() => setHot(gr.id)} onPointerLeave={() => { if (!drag.current) setHot(null); }}
              onPointerDown={(e) => { e.stopPropagation(); e.currentTarget.setPointerCapture?.(e.pointerId); drag.current = { grip: gr, start: structuredClone(geometry), client: { x: e.clientX, y: e.clientY }, k: pxScale(e.currentTarget), scale: t.scale }; setHot(gr.id); }}
              onPointerMove={(e) => {
                const d = drag.current; if (!d || d.grip.id !== gr.id) return;
                let dx = ((e.clientX - d.client.x) * d.k) / d.scale, dy = -((e.clientY - d.client.y) * d.k) / d.scale;
                if (ortho) { if (Math.abs(dx) >= Math.abs(dy)) dy = 0; else dx = 0; }
                onChange(d.grip.apply(d.start, dx, dy));
              }}
              onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}>
              <title>{gr.hint}</title>
            </rect>
            {on ? <text x={s.x + 9} y={s.y - 8} className="text-[10px]" fill="#1F4E79" pointerEvents="none">{gr.hint} · ({fmt(gr.at.x, 2)}; {fmt(gr.at.y, 2)})</text> : null}
          </g>
        );
      })}
    </g>
  );
}
